import React, { useState, useEffect, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { X, BookOpen, ChevronRight, Loader2, Library, ChevronDown, Zap } from 'lucide-react';
import { Button } from './ui/button';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const UNIVERSE_ACCENT = {
  starwars: { ring: 'border-amber-400/40', text: 'text-amber-300', bg: 'bg-amber-500/10', glow: 'hover:shadow-amber-500/20' },
  got: { ring: 'border-red-400/40', text: 'text-red-300', bg: 'bg-red-500/10', glow: 'hover:shadow-red-500/20' },
  lotr: { ring: 'border-emerald-400/40', text: 'text-emerald-300', bg: 'bg-emerald-500/10', glow: 'hover:shadow-emerald-500/20' },
  wh40k: { ring: 'border-purple-400/40', text: 'text-purple-300', bg: 'bg-purple-500/10', glow: 'hover:shadow-purple-500/20' },
};

// Universes still under development — their canon books are hidden from the
// modal entirely (catalogue + tabs). Kept identical to Dashboard's flag list.
const UNDER_DEVELOPMENT_UNIVERSES = new Set();

/**
 * CanonLibraryModal — "Play This Canon" entry point.
 *
 * Props:
 *   open: boolean
 *   onClose: () => void
 *   filterUniverse?: string  // if set, only that universe is shown (per-world button)
 */
export default function CanonLibraryModal({ open, onClose, filterUniverse = null }) {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState(filterUniverse || null);
  const [selectedBook, setSelectedBook] = useState(null);
  const [advancedOpen, setAdvancedOpen] = useState(false);
  const [startBookIdx, setStartBookIdx] = useState(0);
  const [startChIdx, setStartChIdx] = useState(0);
  const [launching, setLaunching] = useState(false);

  useEffect(() => {
    if (!open || data) return;
    setLoading(true);
    axios.get(`${API}/canon-library`, { withCredentials: true })
      .then(res => {
        setData(res.data);
        const allowed = res.data.universes.filter(u => !UNDER_DEVELOPMENT_UNIVERSES.has(u.universe));
        if (!activeTab && allowed.length) {
          const target = filterUniverse && allowed.some(u => u.universe === filterUniverse) ? filterUniverse : allowed[0].universe;
          setActiveTab(target);
        }
      })
      .catch(() => toast.error('Failed to load canon library'))
      .finally(() => setLoading(false));
  }, [open, data, activeTab, filterUniverse]);

  // When user re-opens with a different filter, reset to that tab
  useEffect(() => {
    if (open && filterUniverse) setActiveTab(filterUniverse);
  }, [open, filterUniverse]);

  const visibleUniverses = useMemo(() => {
    if (!data) return [];
    const enabled = data.universes.filter(u => !UNDER_DEVELOPMENT_UNIVERSES.has(u.universe));
    if (filterUniverse) return enabled.filter(u => u.universe === filterUniverse);
    return enabled;
  }, [data, filterUniverse]);

  const activeUniverseData = visibleUniverses.find(u => u.universe === activeTab) || visibleUniverses[0];

  // When picking a different book, reset start position
  const pickBook = (book, seriesBooks) => {
    setSelectedBook({ ...book, _series: seriesBooks });
    const idx = seriesBooks.findIndex(b => b.book_id === book.book_id);
    setStartBookIdx(Math.max(0, idx));
    setStartChIdx(0);
    setAdvancedOpen(false);
  };

  const handleLaunch = async () => {
    if (!selectedBook) return;
    setLaunching(true);
    try {
      const prep = await axios.post(
        `${API}/canon-library/launch`,
        { canon_book_id: selectedBook.book_id },
        { withCredentials: true }
      );
      const info = prep.data;
      // Navigate to character creation with all canon context in state
      navigate('/character/create', {
        state: {
          worldId: info.world_id,
          universe: info.universe,
          timelinePosition: info.timeline_position,
          canonBookId: selectedBook.book_id,
          canonStartBookIndex: startBookIdx,
          canonStartChapterIndex: startChIdx,
          canonBookTitle: selectedBook.title,
          canonSeriesName: info.series_name,
          canonWorldName: info.world_name,
          canonWorldWasCreated: info.was_world_created,
        },
      });
      onClose();
    } catch (e) {
      toast.error('Failed to prepare canon launch');
    } finally {
      setLaunching(false);
    }
  };

  const visibleTotalBooks = useMemo(() => visibleUniverses.reduce((sum, u) => sum + (u.total_books || 0), 0), [visibleUniverses]);

  if (!open) return null;

  const accent = UNIVERSE_ACCENT[activeUniverseData?.universe] || UNIVERSE_ACCENT.starwars;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black/85 backdrop-blur-md z-50 flex items-center justify-center p-4 sm:p-6"
        onClick={() => !launching && onClose()}
        data-testid="canon-library-modal"
      >
        <motion.div
          initial={{ scale: 0.95, y: 30 }}
          animate={{ scale: 1, y: 0 }}
          exit={{ scale: 0.95, y: 30 }}
          onClick={(e) => e.stopPropagation()}
          className="glass-card rounded-2xl w-full max-w-5xl max-h-[90vh] overflow-hidden flex flex-col"
        >
          {/* Header */}
          <div className="flex items-center justify-between px-6 py-4 border-b border-white/5 bg-gradient-to-r from-blue-500/10 via-transparent to-amber-500/10">
            <div className="flex items-center gap-3">
              <Library className={`h-6 w-6 ${accent.text}`} />
              <div>
                <h2 className="text-lg font-bold text-slate-100 font-mono tracking-wide">Canon Library</h2>
                <p className="text-xs text-slate-400 font-mono">
                  {data ? `${visibleTotalBooks} ready-made campaigns` : '...'} · Tier-1 instant load
                </p>
              </div>
            </div>
            <button
              onClick={onClose}
              disabled={launching}
              className="text-slate-400 hover:text-slate-200 disabled:opacity-50"
              data-testid="canon-modal-close"
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          {/* Universe tabs */}
          {!filterUniverse && data && visibleUniverses.length > 1 && (
            <div className="flex gap-2 px-6 py-3 border-b border-white/5 overflow-x-auto">
              {visibleUniverses.map(u => {
                const a = UNIVERSE_ACCENT[u.universe] || UNIVERSE_ACCENT.starwars;
                const active = activeTab === u.universe;
                return (
                  <button
                    key={u.universe}
                    onClick={() => { setActiveTab(u.universe); setSelectedBook(null); }}
                    className={`px-3 py-1.5 rounded-lg font-mono text-xs whitespace-nowrap transition-all border ${active ? `${a.bg} ${a.ring} ${a.text}` : 'bg-slate-900/50 border-white/5 text-slate-400 hover:text-slate-200'}`}
                    data-testid={`canon-tab-${u.universe}`}
                  >
                    {u.label} <span className="opacity-60">({u.total_books})</span>
                  </button>
                );
              })}
            </div>
          )}

          {/* Body — split: book list + detail panel */}
          <div className="flex-1 overflow-hidden flex flex-col md:flex-row">
            {/* Left: book list */}
            <div className="flex-1 overflow-y-auto p-4 space-y-4 md:border-r border-white/5">
              {loading ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="h-8 w-8 animate-spin text-blue-400" />
                </div>
              ) : !activeUniverseData ? (
                <div className="text-slate-500 text-center py-8 font-mono text-sm">No books available.</div>
              ) : (
                activeUniverseData.series.map(series => (
                  <div key={series.series_name}>
                    <h3 className={`text-xs font-mono font-bold mb-2 ${accent.text}`}>
                      {series.series_name}
                      <span className="text-slate-500 ml-2 font-normal">({series.book_count} book{series.book_count !== 1 ? 's' : ''})</span>
                    </h3>
                    <div className="space-y-1.5">
                      {series.books.map(book => {
                        const selected = selectedBook?.book_id === book.book_id;
                        return (
                          <button
                            key={book.book_id}
                            onClick={() => pickBook(book, series.books)}
                            className={`w-full text-left p-3 rounded-lg border transition-all flex items-start gap-3 ${selected ? `${accent.bg} ${accent.ring}` : 'bg-slate-900/40 border-white/5 hover:border-white/15'}`}
                            data-testid={`canon-book-${book.book_id}`}
                          >
                            <BookOpen className={`h-4 w-4 mt-0.5 flex-shrink-0 ${selected ? accent.text : 'text-slate-500'}`} />
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2 flex-wrap">
                                <span className="text-sm font-bold text-slate-200 truncate">{book.title}</span>
                                {book.timeline_start && (
                                  <span className="text-[10px] font-mono text-amber-400/70 bg-amber-500/5 px-1.5 py-0.5 rounded border border-amber-500/10">
                                    {book.timeline_start}
                                  </span>
                                )}
                              </div>
                              <div className="text-[10px] text-slate-500 font-mono mt-0.5 truncate">
                                {book.author && <>by {book.author} · </>}{book.chapter_count} chapter{book.chapter_count !== 1 ? 's' : ''}
                              </div>
                            </div>
                            {selected && <ChevronRight className={`h-4 w-4 ${accent.text}`} />}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                ))
              )}
            </div>

            {/* Right: detail panel */}
            <div className="md:w-[42%] md:flex-shrink-0 overflow-y-auto p-5 bg-black/30">
              {!selectedBook ? (
                <div className="h-full flex flex-col items-center justify-center text-center text-slate-500 font-mono text-xs py-12">
                  <BookOpen className="h-10 w-10 mb-3 opacity-30" />
                  <p>Pick a book to start a ready-made campaign.</p>
                  <p className="mt-1 opacity-60">Loads instantly · zero LLM cost.</p>
                </div>
              ) : (
                <>
                  <h3 className={`text-base font-bold mb-1 ${accent.text}`}>{selectedBook.title}</h3>
                  <p className="text-xs text-slate-500 font-mono mb-3">
                    {selectedBook.author && <>by {selectedBook.author} · </>}
                    {selectedBook.era && <>{selectedBook.era} · </>}
                    {selectedBook.timeline_start}{selectedBook.timeline_end && selectedBook.timeline_end !== selectedBook.timeline_start ? ` → ${selectedBook.timeline_end}` : ''}
                  </p>
                  <p className="text-xs text-slate-300 leading-relaxed mb-4">{selectedBook.book_summary}</p>

                  {/* Advanced: pick starting point */}
                  {selectedBook._series?.length > 1 && (
                    <div className="mb-4 border border-white/5 rounded-lg overflow-hidden">
                      <button
                        onClick={() => setAdvancedOpen(o => !o)}
                        className="w-full px-3 py-2 flex items-center justify-between text-xs font-mono text-slate-400 hover:text-slate-200 hover:bg-white/5 transition-all"
                        data-testid="canon-advanced-toggle"
                      >
                        <span>Advanced: pick starting point</span>
                        <ChevronDown className={`h-4 w-4 transition-transform ${advancedOpen ? 'rotate-180' : ''}`} />
                      </button>
                      {advancedOpen && (
                        <div className="p-3 space-y-2 bg-black/30 border-t border-white/5">
                          <label className="block text-[10px] text-slate-500 font-mono uppercase">Start at book</label>
                          <select
                            value={startBookIdx}
                            onChange={(e) => { setStartBookIdx(parseInt(e.target.value, 10)); setStartChIdx(0); }}
                            className="w-full bg-slate-950 border border-white/10 text-slate-200 text-xs rounded px-2 py-1.5 font-mono"
                            data-testid="canon-start-book-select"
                          >
                            {selectedBook._series.map((b, i) => (
                              <option key={b.book_id} value={i}>{i + 1}. {b.title}</option>
                            ))}
                          </select>
                          <label className="block text-[10px] text-slate-500 font-mono uppercase pt-1">Start at chapter</label>
                          <select
                            value={startChIdx}
                            onChange={(e) => setStartChIdx(parseInt(e.target.value, 10))}
                            className="w-full bg-slate-950 border border-white/10 text-slate-200 text-xs rounded px-2 py-1.5 font-mono"
                            data-testid="canon-start-chapter-select"
                          >
                            {Array.from({ length: selectedBook._series[startBookIdx]?.chapter_count || 1 }).map((_, i) => (
                              <option key={i} value={i}>Chapter {i + 1}</option>
                            ))}
                          </select>
                        </div>
                      )}
                    </div>
                  )}

                  <Button
                    onClick={handleLaunch}
                    disabled={launching}
                    className={`w-full ${accent.bg} hover:brightness-125 ${accent.text} ${accent.ring} border font-mono text-sm`}
                    data-testid="canon-launch-btn"
                  >
                    {launching ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Zap className="h-4 w-4 mr-2" />}
                    {launching ? 'Preparing...' : 'Play This Canon'}
                  </Button>
                  <p className="text-[10px] text-slate-500 font-mono mt-2 text-center leading-relaxed">
                    Reuses your existing world for this universe, or creates one named after the book.
                    <br />Next: build your character.
                  </p>
                </>
              )}
            </div>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}
